import { useRef, useState } from "react";
import { Carousel, Dialog, Radio } from "@material-tailwind/react";

import "@geoapify/geocoder-autocomplete/styles/minimal.css";

//import services
import { useAddAdvertiseMutation } from "../../Services/AdvertiseService";

//import json
import phoneCodes from "../../Util/phone.json";
import { formatPriceEuroEnd, formatNumber } from "../../Util/priceFormatter";
import countryCodes from "../../Util/country.json";
import { Constant } from "../../Constant";

//import custom components
import { Button, LinkButton } from "../Buttons";
import {
  TextInput,
  Dropdown,
  Checkbox,
  DropdownInput,
  RangeSliderComponent,
} from "../FormElements";
import { CountrySelect } from "../CountrySelect";

import { Loader } from "../Loader";
import { Alert } from "../Alert";

//import assets
import DocumentIcon from "../../Assets/document";
import RedClose from "../../Assets/RedClose";
import Room from "../../Assets/Room";
import MapPin from "../../Assets/MapPin";
import Image from "../../Assets/Image";
import Documents from "../../Assets/Documents";
import { useApp } from "../../Context";
import { useLocation, useNavigate } from "react-router-dom";
import React, { useEffect } from "react";

// SearchIntentModal - wird von mehreren Modals verwendet
export const SearchIntentModal = ({ open, handler, onBrowse, onCreateRequest, validationError }) => {
  return (
    <Dialog
      open={open}
      handler={handler}
      size="md"
      className="bg-background"
      animate={{
        mount: { scale: 1, y: 0 },
        unmount: { scale: 0.9, y: -100 },
      }}
    >
      <div className="p-8">
        {/* Validierungsfehler Anzeige */}
        {validationError && (
          <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-r-lg animate-pulse">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium font-helvetica text-red-800">
                  Pflichtfelder fehlen
                </h3>
                <div className="mt-2 text-sm font-helvetica text-red-700">
                  <p className="mb-2">Bitte fülle noch folgende Felder aus, um ein Gesuch zu erstellen:</p>
                  <ul className="list-disc list-inside space-y-1">
                    {validationError.missingFields?.map((field, idx) => (
                      <li key={idx}>{field}</li>
                    ))}
                  </ul>
                  <p className="mt-3 text-xs">
                    Diese Informationen benötigen wir, damit Makler dich kontaktieren können.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="text-center mb-6">
          <h2 className="text-3xl font-georgia text-primary mb-3">
            Wie möchtest du fortfahren?
          </h2>
          <p className="text-lg font-helvetica text-primary">
            Wähle die passende Option für deine Immobiliensuche
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-8">
          {/* Nur stöbern Option */}
          <div 
            onClick={onBrowse}
            className="bg-white rounded-xl p-6 border-2 border-primary/20 hover:border-primary hover:shadow-xl transition-all duration-300 cursor-pointer group"
          >
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-16 h-16 rounded-full bg-lightGreen flex items-center justify-center group-hover:bg-primary transition-colors duration-300">
                <svg 
                  className="w-8 h-8 text-primary group-hover:text-white transition-colors duration-300" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-helvetica font-bold text-primary mb-2">
                  Nur stöbern
                </h3>
                <p className="text-sm font-helvetica text-primary/80">
                  Schaue dir verfügbare Immobilien an, ohne ein Gesuch zu erstellen
                </p>
              </div>
              <div className="mt-2">
                <span className="inline-block bg-lightGreen text-primary text-xs font-helvetica px-3 py-1 rounded-full">
                  Schnell & Unverbindlich
                </span>
              </div>
            </div>
          </div>

          {/* Gesuch erstellen Option */}
          <div 
            onClick={() => {
              console.log('Gesuch erstellen clicked!');
              console.log('onCreateRequest function:', onCreateRequest);
              onCreateRequest();
            }}
            className="bg-gradient-to-br from-primary to-primary/90 rounded-xl p-6 border-2 border-primary hover:shadow-2xl hover:scale-105 transition-all duration-300 cursor-pointer relative overflow-hidden group"
          >
            <div className="absolute top-0 right-0 bg-white/20 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
              EMPFOHLEN
            </div>
            <div className="flex flex-col items-center text-center gap-4 relative z-10">
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <svg 
                  className="w-8 h-8 text-primary" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-helvetica font-bold text-white mb-2">
                  Gesuch erstellen
                </h3>
                <p className="text-sm font-helvetica text-white/90">
                  Lass Makler im Umkreis dein Traumobjekt finden
                </p>
              </div>
              <div className="mt-2 space-y-2">
                <div className="flex items-center gap-2 text-white text-xs font-helvetica">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  100% Kostenlos
                </div>
                <div className="flex items-center gap-2 text-white text-xs font-helvetica">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Kein Account nötig
                </div>
                <div className="flex items-center gap-2 text-white text-xs font-helvetica">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Direkte Makler-Vermittlung
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm font-helvetica text-primary/70">
            Du kannst deine Entscheidung jederzeit ändern
          </p>
        </div>

        <button
          onClick={handler}
          className="mt-4 mx-auto block text-primary/60 hover:text-primary font-helvetica text-sm underline"
        >
          Abbrechen
        </button>
      </div>
    </Dialog>
  );
};

export const FilterModal = ({ open, handler }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { setFilters, filters, setSearchMode } = useApp();
  const [showIntentModal, setShowIntentModal] = useState(false);
  const [usableAreaManuallySet, setUsableAreaManuallySet] = useState(false);
  const [validationError, setValidationError] = useState(null);

  // Füge das useEffect hier ein, direkt nach der Deklaration von setFilters und filters
  useEffect(() => {
    // Wenn sich der advertise_type ändert, Preisbereich automatisch anpassen
    if (filters.advertise_type === "Mieten") {
      // Setze standardmäßige Werte für Mieten
      setFilters(prev => ({
        ...prev,
        minPrice: 100,
        maxPrice: 5000
      }));
    } else {
      // Setze standardmäßige Werte für Kaufen
      setFilters(prev => ({
        ...prev,
        minPrice: 10000,
        maxPrice: 5000000
      }));
    }
  }, [filters.advertise_type]); // Nur wenn sich advertise_type ändert

  // Synchronisiere Nutzfläche mit Fläche, wenn Fläche gesetzt wird und Nutzfläche nicht manuell geändert wurde
  useEffect(() => {
    if (!usableAreaManuallySet && (filters.minArea || filters.maxArea)) {
      setFilters(prev => ({
        ...prev,
        minUsableArea: filters.minArea,
        maxUsableArea: filters.maxArea
      }));
    }
  }, [filters.minArea, filters.maxArea, usableAreaManuallySet]);

  //handle input values
  const handleInput = (e) => {
    const { name, value } = e.target;

    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };


  // Erstelle eine Hilfsfunktion zur Konvertierung des Jahres in ein volles Datum
const convertYearToFullDate = (year) => {
  if (!year) return "";
  return `${year}-01-01`; // Format YYYY-01-01 für HTML input type="date"
};

// Extrahiere nur das Jahr aus einem Datum
const extractYearFromDate = (dateString) => {
  if (!dateString) return "";
  return dateString.split('-')[0]; // Nimmt nur das Jahr aus YYYY-MM-DD
};
  
  //handle checkbox values
  const handleCheckBox = (val) => {
    const isChecked = filters.furnishing.includes(val);

    if (isChecked) {
      const updatedFurnishing = filters.furnishing.filter(
        (item) => item !== val
      );
      setFilters((prevState) => ({
        ...prevState,
        furnishing: updatedFurnishing,
      }));
    } else {
      setFilters((prevState) => ({
        ...prevState,
        furnishing: [...prevState.furnishing, val],
      }));
    }
  };

  const handlePriceRange = (val) => {
    setFilters((prevState) => ({
      ...prevState,
      minPrice: val[0],
      maxPrice: val[1],
    }));
  };

  const handleAreaRange = (val) => {
    setFilters((prevState) => ({
      ...prevState,
      minArea: val[0],
      maxArea: val[1],
    }));
  };

  const handleUsableAreaRange = (val) => {
    setUsableAreaManuallySet(true); // Markiere, dass Nutzfläche manuell geändert wurde
    setFilters((prevState) => ({
      ...prevState,
      minUsableArea: val[0],
      maxUsableArea: val[1],
    }));
  };

  const handleConstructionYearRange = (val) => {
    setFilters((prevState) => ({
      ...prevState,
      minConstructionYear: convertYearToFullDate(val[0]),
      maxConstructionYear: convertYearToFullDate(val[1]),
    }));
  };

  const handleFilter = (e) => {
    e.preventDefault();
    
    // Schließe das FilterModal und zeige das SearchIntentModal
    handler();
    setShowIntentModal(true);
  };

  const handleIntentModalClose = () => {
    setShowIntentModal(false);
  };

  const handleBrowse = () => {
    setSearchMode('browse');
    setShowIntentModal(false);
    if (pathname !== "/filter") {
      navigate("/filter");
    }
  };

  const handleCreateRequest = () => {
    // Debug: Zeige Filter-Werte
    console.log('Filter values:', filters);
    console.log('Name:', filters.name, 'Type:', typeof filters.name, 'Length:', filters.name?.length);
    console.log('Mail:', filters.mail, 'Type:', typeof filters.mail, 'Length:', filters.mail?.length);
    
    // Prüfe ob Name und E-Mail ausgefüllt sind (auch auf Whitespace prüfen)
    const nameEmpty = !filters.name || filters.name.trim() === '';
    const mailEmpty = !filters.mail || filters.mail.trim() === '';
    
    if (nameEmpty || mailEmpty) {
      // Zeige inline Fehlermeldung
      const missingFields = [];
      if (nameEmpty) missingFields.push("Vor- und Nachname");
      if (mailEmpty) missingFields.push("E-Mail");
      
      setValidationError({
        missingFields: missingFields
      });
      return;
    }
    
    // Validierung erfolgreich - lösche Fehlermeldung und setze SearchMode
    setValidationError(null);
    setSearchMode('create-request');
    setShowIntentModal(false);
    
    // Navigiere zur Filter-Seite - die Daten werden automatisch über useGetFilterAdvertiseQuery gesendet
    if (pathname !== "/filter") {
      navigate("/filter");
    }
  };

  const clearFilter = () => {
    setUsableAreaManuallySet(false); // Setze das Flag zurück
    setFilters({
      city: "",
      rooms: "",
      minArea: "",
      maxArea: "",
      minPrice: "",
      maxPrice: "",
      furnishing: "",
      minConstructionYear: "",
      maxConstructionYear: "",
      minUsableArea: "",
      maxUsableArea: "",
      advertise_type: "",
      category: "",
      name: "",
      mail: "",
      notes: "",
    });
  };

  const handleSelectCity = (city) => {
    setFilters((prevState) => ({
      ...prevState,
      city: city,
    }));
  };

  return (
    <Dialog
      open={open}
      handler={handler}
      className="bg-background"
      animate={{
        mount: { scale: 1, y: 0 },
        unmount: { scale: 0.9, y: -100 },
      }}
    >
      <div className="p-3 flex justify-between items-center">
        <p className="text-[18px] font-helvetica font-bold text-primary">
          Alle Filter (Gesuch verfeinern)
        </p>
        <a
          onClick={handler}
          className="w-8 h-8 flex justify-center items-center rounded-full bg-lightRed"
        >
          <RedClose />
        </a>
      </div>
      <div className="w-full h-[1px] bg-[#FF6A2B]" />
      <div className="p-3 lg:max-h-[70vh] max-h-[60vh] overflow-y-auto overflow-x-hidden">
        <form className="flex flex-col gap-3 pb-4" onSubmit={handleFilter}>
          <div>
            <p className="text-[16px] font-helvetica font-bold text-primary">
              Typ
            </p>
            <div className="flex gap-16">
              <Radio
                name="Werbetyp"
                label={
                  <p className="font-helvetica font-normal text-primary">
                    Mieten
                  </p>
                }
                value="Mieten" // Set the value to the desired option
                checked={filters.advertise_type === "Mieten"} // Add a checked prop to indicate the current state
                onChange={() =>
                  setFilters({ ...filters, advertise_type: "Mieten" })
                }
                icon={<div className="w-3 h-3 rounded-full bg-primary" />}
                className="border-2 border-primary bg-white p-0 m-0 transition-all hover:before:opacity-0 focus:border-primary"
                ripple={false}
              />

              <Radio
                name="Werbetyp"
                value="Kaufen" // Set the value to the desired option
                checked={filters.advertise_type === "Kaufen"} // Add a checked prop to indicate the current state
                onChange={() =>
                  setFilters({ ...filters, advertise_type: "Kaufen" })
                }
                label={
                  <p className="font-helvetica font-normal text-primary">
                    Kaufen
                  </p>
                }
                icon={<div className="w-3 h-3 rounded-full bg-primary" />}
                className="border-2 border-primary bg-white p-0 transition-all hover:before:opacity-0 focus:border-primary"
                ripple={false}
              />
            </div>
          </div>
          
          {/* Visueller Trenner */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF6A2B4D] to-transparent my-2" />
          
          <CountrySelect
            name="city"
            label="Standort"
            placeholder="Suchen"
            handleSelect={handleSelectCity}
          />
          
          {/* Visueller Trenner */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF6A2B4D] to-transparent my-2" />
          
          <RangeSliderComponent
            name="Preis"
            value={[filters.minPrice, filters.maxPrice]}
            min={filters.advertise_type === "Mieten" ? 100 : 10000}
            max={filters.advertise_type === "Mieten" ? 5000 : 5000000}
            step={10}
            onInput={(val) => handlePriceRange(val)}
            labels={["Preis von", "Preis bis"]}
          />
          {/* <RangeSliderComponent
            name="Fläche"
            value={[filters.minArea, filters.maxArea]}
            min={1}
            max={50}
            onInput={(val) => handleAreaRange(val)}
            labels={["Fläche von", "Fläche bis"]}
          /> */}
          
          {/* Visueller Trenner */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF6A2B4D] to-transparent my-2" />
          
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              name="name"
              value={filters.name}
              onChange={handleInput}
              label="Vor- und Nachname"
              type="name"
              placeholder="Vor- und Nachname"
            />
            <TextInput
              name="mail"
              value={filters.mail}
              onChange={handleInput}
              label="E-Mail"
              type="email"
              placeholder="E-Mail"
            />
          </div>

          <div className="grid grid-cols-1 gap-3">
            <TextInput
              name="notes"
              value={filters.notes}
              onChange={handleInput}
              label="Anmerkungen zum Gesuch"
              type="text"
              placeholder="z.B. großer Garten"
            />
          </div>
          
          {/* Visueller Trenner */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF6A2B4D] to-transparent my-2" />
          
          <RangeSliderComponent
            name="Fläche"
            value={[filters.minArea || 20, filters.maxArea || 500]}
            min={20}
            max={500}
            step={5}
            onInput={(val) => handleAreaRange(val)}
            labels={["Fläche von (m²)", "Fläche bis (m²)"]}
          />
          
          {/* Visueller Trenner */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF6A2B4D] to-transparent my-2" />
          
          <div className="grid grid-cols-2 gap-3">
            {/* <select>

            </select> */}
            <Dropdown
              data={Constant.NEW_CATEGORY}
              value={filters.category}
              name="category"
              label="Kategorie"
              onChange={handleInput}
              required
            />
            <Dropdown
              data={
                Constant.NEW_CATEGORY.find(
                  (item) => item.value === filters.category
                )?.sub
              }
              value={filters.sub_category}
              name="sub_category"
              label="Typ"
              onChange={handleInput}
              required
            />
          </div>
          
          {/* Visueller Trenner */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF6A2B4D] to-transparent my-2" />
          
          <RangeSliderComponent
            name="Baujahr"
            value={[
              extractYearFromDate(filters.minConstructionYear) || 1800,
              extractYearFromDate(filters.maxConstructionYear) || new Date().getFullYear()
            ]}
            min={1800}
            max={new Date().getFullYear()}
            step={1}
            onInput={(val) => handleConstructionYearRange(val)}
            labels={["Baujahr von", "Baujahr bis"]}
          />
          
          {/* Visueller Trenner */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF6A2B4D] to-transparent my-2" />
          
          <RangeSliderComponent
            name="Nutzfläche"
            value={[filters.minUsableArea || 20, filters.maxUsableArea || 500]}
            min={20}
            max={500}
            step={5}
            onInput={(val) => handleUsableAreaRange(val)}
            labels={["Nutzfläche von (m²)", "Nutzfläche bis (m²)"]}
          />
          
          {/* Visueller Trenner */}
          <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#FF6A2B4D] to-transparent my-2" />
          
          <p className="text-[16px] font-helvetica font-bold text-primary">
            Ausstattung
          </p>
          <div className="grid grid-cols-2 gap-3 ">
            {Constant.FURNISHING.map((item, idx) => (
              <Checkbox
                key={idx}
                label={item}
                onClick={() => handleCheckBox(item)}
                checked={filters.furnishing.includes(item)}
              />
            ))}
          </div>
          <div className="w-full h-[1px] bg-[#FF6A2B4D]" />
          <div className="flex justify-between gap-5">
            <button
              type="button"
              className="font-light font-helvetica border border-1 border-red-600 text-[16px] px-8 py-3 rounded-full w-full text-center"
              onClick={clearFilter}
            >
              Filter zurücksetzen
            </button>
            <Button title="Suchen" type="submit" variant="primary" full />
          </div>
        </form>
      </div>
      
      <SearchIntentModal 
        open={showIntentModal} 
        handler={handleIntentModalClose}
        onBrowse={handleBrowse}
        onCreateRequest={handleCreateRequest}
        validationError={validationError}
      />
    </Dialog>
  );
};

export const PreviewModal = ({ open, handleClose, data }) => {
  return (
    <Dialog
      open={open}
      handler={handleClose}
      size="lg"
      className="bg-background max-h-[95vh] overflow-hidden"
      animate={{
        mount: { scale: 1, y: 0 },
        unmount: { scale: 0.9, y: -100 },
      }}
    >
      <div className="p-3 flex justify-between items-center">
        <p className="text-[18px] font-helvetica font-bold text-primary">
          Inserat
        </p>
        <a
          onClick={handleClose}
          className="w-8 h-8 flex justify-center items-center rounded-full bg-lightRed"
        >
          <RedClose />
        </a>
      </div>
      <div className="w-full h-[1px] bg-[#a6a8a7]" />
      <div className="p-3 lg:max-h-[calc(95vh-120px)] max-h-[calc(95vh-120px)] overflow-y-auto overflow-x-hidden">
        <div className="flex flex-col gap-2 pb-6">
          <div className="lg:h-96 sm:h-44">
            <Carousel>
              {data.photos.map((item, idx) => (
                <img
                  key={idx}
                  src={`${process.env.REACT_APP_API_URL}uploads/${item}`}
                  alt="image 1"
                  className="h-full w-full object-cover"
                />
              ))}
            </Carousel>
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-lg font-helvetica font-bold text-primary">
              {data.title}
            </p>
            <p className="text-[16px] font-normal font-helvetica text-primary">
              {data.description}
            </p>
            <div className="flex lg:flex-row sm:flex-wrap gap-4 lg:items-center">
              <span className="flex items-center gap-2">
                <Room />
                <p className="text-[16px] font-normal font-helvetica text-primary">
                  {data.rooms} Zimmer
                </p>
              </span>

              {/* <p className="text-[16px] font-normal font-helvetica text-primary">
                3 bathrooms
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                terrace
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                garage
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                114m living space
              </p> */}
            </div>
            <span className="flex items-center gap-2">
              <MapPin />
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Zweibrückenstraße 12 80331 Munich, Germany
              </p>
            </span>
            <span className="flex items-center gap-2">
              <DocumentIcon />
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Energieausweis
              </p>
            </span>
            <p className="text-lg font-helvetica font-bold text-primary">
              Kosten
            </p>
            <div className="grid grid-cols-2">
              <p className="text-[16px] font-bold font-helvetica text-primary">
                Total
              </p>
              <p className="text-[16px] font-bold font-helvetica text-primary text-end">
                {formatPriceEuroEnd(data.price)}
              </p>
            </div>
            <div className="w-full h-[1px] bg-[#a6a8a7]" />
            <p className="text-lg font-helvetica font-bold text-primary">
              Informationen
            </p>
            <div className="grid grid-cols-2">
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Zimmer
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.rooms}
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Fläche
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.area}
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Adresse
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.address}
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Stadt
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.city}
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Baudatum
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.construction_year}
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Letzte Renovierung
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.last_renovation}
              </p>
            </div>
            <div className="w-full h-[1px] bg-[#a6a8a7]" />
            <p className="text-lg font-helvetica font-bold text-primary">
              Ausstattung
            </p>
            <div className="flex flex-wrap gap-2">
              {data.furnishing.map((item, idx) => (
                <p
                  key={idx}
                  className="text-[16px] font-normal font-helvetica text-primary"
                >
                  {item.trim()}
                  <span className="text-gray">{" | "}</span>
                </p>
              ))}
            </div>
            <p className="text-lg font-helvetica font-bold text-primary">
              Kontaktinformationen
            </p>
            <div className="grid grid-cols-2">
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Telefonnummer
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.telephone_number}
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                Name
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.sure_name}
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary">
                E-Mail Adresse
              </p>
              <p className="text-[16px] font-normal font-helvetica text-primary text-end">
                {data.email}
              </p>
            </div>
          </div>
        </div>
        <div className="w-full h-[1px] bg-[#a6a8a7]" />
        <div className="mt-3 flex justify-center">
          <LinkButton
            href={`mailto:${data.email}`}
            target="_"
            title="E-Mail senden"
            variant="primary"
          />
        </div>
      </div>
    </Dialog>
  );
};

export const AdvertiseModal = ({ open, handler }) => {
  const formRef = useRef(null);
  //services
  const [addAdvertise, { isLoading }] = useAddAdvertiseMutation();

  //advertise state
  const [state, setState] = useState({
    title: "",
    description: "",
    rooms: "",
    address: "",
    city: "",
    area: "",
    price: "",
    availability: "",
    advertise_type: "",
    additional_cost: false,
    house_type: "",
    apartment_type: "",
    construction_year: "",
    documents: [],
    photos: [],
    furnishing: [],
    sure_name: "",
    email: "",
    telephone_number: "",
    last_renovation: "",
    category: "",
    sub_category: "",
  });
  const [error, setError] = useState({
    type: "",
    text: "",
    isShow: false,
  });

  //handle input values
  const handleInput = (e) => {
    const { name, value } = e.target;

    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  //handle checkbox values
  const handleCheckBox = (val) => {
    const isChecked = state.furnishing.includes(val);

    if (isChecked) {
      const updatedFurnishing = state.furnishing.filter((item) => item !== val);
      setState((prevState) => ({
        ...prevState,
        furnishing: updatedFurnishing,
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        furnishing: [...prevState.furnishing, val],
      }));
    }
  };

  const handleSelectCity = (city) => {
    setState((prevState) => ({
      ...prevState,
      city: city,
    }));
  };

  //submit advertisement
  const handleSubmit = async (e) => {
    e.preventDefault();
    const frmData = new FormData();
    frmData.append("title", state.title);
    frmData.append("description", state.description);
    frmData.append("rooms", state.rooms);
    frmData.append("address", state.address);
    frmData.append("city", state.city);
    frmData.append("area", state.area);
    frmData.append("price", state.price);
    frmData.append("additional_cost", state.additional_cost);
    frmData.append("apartment_type", state.apartment_type);
    frmData.append("house_type", state.house_type);
    frmData.append("construction_year", state.construction_year);
    frmData.append("availability", state.availability);
    for (let i = 0; i < state.photos?.length; i++) {
      frmData.append("photos", state.photos[i]);
    }
    for (let i = 0; i < state.documents?.length; i++) {
      frmData.append("documents", state.documents[i]);
    }
    frmData.append("furnishing", state.furnishing);
    frmData.append("sure_name", state.sure_name);
    frmData.append("email", state.email);
    frmData.append("telephone_number", state.telephone_number);
    frmData.append("last_renovation", state.last_renovation);
    frmData.append("advertise_type", state.advertise_type);
    frmData.append("category", state.category);
    frmData.append("sub_category", state.sub_category);

    try {
      const res = await addAdvertise(frmData);

      //if failed
      if (res.error) {
        setError({
          type: "Warning",
          text: res.error.data.message,
          isShow: true,
        });
        return;
      }
      //if success
      if (res.data.code === 200) {
        setError({
          type: "Success",
          text: res.data.message,
          isShow: true,
        });
        clearForm();
      }
    } catch (error) {
      console.log("catch error :", error);
    }
  };

  //clear form
  const clearForm = () => {
    setState({
      title: "",
      description: "",
      rooms: "",
      address: "",
      area: "",
      price: "",
      availability: "",
      additional_cost: false,
      house_type: "",
      apartment_type: "",
      construction_year: "",
      documents: [],
      photos: [],
      furnishing: [],
      sure_name: "",
      email: "",
      telephone_number: "",
      last_renovation: "",
      advertise_type: "",
      category: "",
      city: "",
      sub_category: "",
    });
  };

  return (
    <Dialog
      open={open}
      size="lg"
      className="bg-background max-h-[95vh] overflow-hidden"
      animate={{
        mount: { scale: 1, y: 0 },
        unmount: { scale: 0.9, y: -100 },
      }}
    >
      <div className="p-3 flex justify-between items-center">
        <p className="text-[18px] font-helvetica font-bold text-primary">
          Neues Inserat
        </p>
        <a
          onClick={handler}
          className="w-8 h-8 flex justify-center items-center rounded-full bg-lightRed"
        >
          <RedClose />
        </a>
      </div>
      <div className="w-full h-[1px] bg-[#FF6A2B]" />
      <div className="p-3 lg:max-h-[calc(95vh-120px)] max-h-[calc(95vh-120px)] overflow-y-auto overflow-x-hidden">
        <p className="text-[20px] font-helvetica font-bold text-primary">
          Alle Attribute
        </p>
        <form
          ref={formRef}
          className="flex flex-col gap-3 mt-5 pb-6"
          onSubmit={handleSubmit}
        >
          <TextInput
            name="title"
            label="Titel"
            placeholder="Eingeben..."
            value={state.title}
            onChange={handleInput}
            required
          />
          <TextInput
            name="description"
            label="Beschreibung"
            placeholder="Eingeben..."
            value={state.description}
            onChange={handleInput}
            multiline
            required
          />
          <div className="grid grid-cols-2 gap-3">
            {/* <select>

            </select> */}
            <Dropdown
              data={Constant.NEW_CATEGORY}
              value={state.category}
              name="category"
              label="Kategorie"
              onChange={handleInput}
              required
            />
            <Dropdown
              data={
                Constant.NEW_CATEGORY.find(
                  (item) => item.value === state.category
                )?.sub
              }
              value={state.sub_category}
              name="sub_category"
              label="Typ"
              onChange={handleInput}
              required
            />
          </div>
          <Dropdown
            data={Constant.ROOMS}
            name="rooms"
            label="Zimmer"
            value={state.rooms}
            onChange={handleInput}
          />
          <TextInput
            name="area"
            label="Fläche (m²)"
            placeholder="50"
            value={state.area}
            onChange={handleInput}
            required
          />

          <TextInput
            name="address"
            label="Adresse"
            placeholder="z.B. Musterstraße 12, 12345 Musterstadt"
            value={state.address}
            onChange={handleInput}
            required
          />
          <CountrySelect
            name="city"
            label="Standort"
            placeholder="Suchen"
            handleSelect={handleSelectCity}
            required
          />
          <div>
            <p className="text-[16px] font-helvetica font-bold text-primary italic">
              Verfügbarkeit
            </p>
            <div className="flex gap-16">
              <Radio
                name="Verfügbarkeit"
                label={
                  <p className="text-[16px] font-helvetica font-normal">
                    Sofort
                  </p>
                }
                value={state.availability}
                onChange={() => setState({ ...state, availability: "Sofort" })}
                icon={<div className="w-3 h-3 rounded-full bg-primary" />}
                className="border-2 border-primary bg-white p-0 m-0 transition-all hover:before:opacity-0 focus:border-primary"
                ripple={false}
              />
              <Radio
                name="Verfügbarkeit"
                onChange={() =>
                  setState({ ...state, availability: "Nach Vereinbarung" })
                }
                label={
                  <p className="text-[16px] font-helvetica font-normal">
                    Nach Vereinbarung
                  </p>
                }
                value={state.availability}
                icon={<div className="w-3 h-3 rounded-full bg-primary" />}
                className="border-2 border-primary bg-white p-0 transition-all hover:before:opacity-0 focus:border-primary"
                ripple={false}
              />
            </div>
          </div>
          <div>
            <p className="text-[16px] font-helvetica font-bold text-primary italic">
              Typ
            </p>
            <div className="flex gap-16">
              <Radio
                name="Werbetyp"
                label={
                  <p className="text-[16px] font-helvetica font-normal">
                    Mieten
                  </p>
                }
                value={state.advertise_type}
                onChange={() =>
                  setState({ ...state, advertise_type: "Mieten" })
                }
                icon={<div className="w-3 h-3 rounded-full bg-primary" />}
                className="border-2 border-primary bg-white p-0 m-0 transition-all hover:before:opacity-0 focus:border-primary"
                ripple={false}
              />
              <Radio
                name="Werbetyp"
                onChange={() =>
                  setState({ ...state, advertise_type: "Kaufen" })
                }
                label={
                  <p className="text-[16px] font-helvetica font-normal">
                    Kaufen
                  </p>
                }
                value={state.advertise_type}
                icon={<div className="w-3 h-3 rounded-full bg-primary" />}
                className="border-2 border-primary bg-white p-0 transition-all hover:before:opacity-0 focus:border-primary"
                ripple={false}
              />
            </div>
          </div>
          <div className="w-full h-[1px] bg-[#FF6A2B4D]" />
          <p className="text-[20px] font-helvetica font-bold text-primary">
            Preis
          </p>
          <div className="flex flex-col gap-3">
            <DropdownInput
              name="price"
              value={state.price}
              label="Kosten"
              placeholder="Eingeben"
              data={countryCodes.map(({ code }) => ({
                label: code,
                value: code,
              }))}
              selectedValue="EUR"
              onChangeText={handleInput}
              required
            />
            <Checkbox
              name="additional_cost"
              label="Nebenkosten enthalten"
              checked={state.additional_cost}
              onClick={() =>
                setState({ ...state, additional_cost: !state.additional_cost })
              }
            />
          </div>
          <div className="w-full h-[1px] bg-[#FF6A2B4D]" />
          <p className="text-[20px] font-helvetica font-bold text-primary">
            Details
          </p>


          <div className="grid grid-cols-2 gap-3">
            <TextInput
              value={state.construction_year}
              name="construction_year"
              label="Baudatum"
              type="date"
              onChange={handleInput}
              required
            />
            <TextInput
              value={state.last_renovation}
              name="last_renovation"
              label="Letzte Renovierung"
              onChange={handleInput}
              type="date"
            />
          </div>
          <p className="text-[20px] font-helvetica font-bold text-primary">
            Ausstattung
          </p>
          <div className="grid grid-cols-2 gap-3 ">
            {Constant.FURNISHING.map((item, idx) => (
              <Checkbox
                key={idx}
                label={item}
                onClick={() => handleCheckBox(item)}
                checked={state.furnishing.includes(item)}
              />
            ))}
          </div>
          <div className="w-full h-[1px] bg-[#FF6A2B4D]" />
          <p className="text-[20px] font-helvetica font-bold text-primary">
            Fotos
          </p>
          
          {/* Foto Upload Bereich */}
          <label className="border border-dashed border-primary w-full h-32 rounded-md flex flex-col justify-center items-center gap-2 bg-[#FF6A2B1A] cursor-pointer hover:bg-[#FF6A2B2A] transition-colors">
            <Image />
            <p className="text-sm font-helvetica text-primary">
              Fotos hochladen
            </p>
            <p className="text-xs font-helvetica text-primary">
              PNG, JPG or JPEG
            </p>
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  setState({ ...state, photos: Array.from(e.target.files) });
                }
              }}
              multiple
              accept="image/png, image/jpeg"
            />
          </label>

          {/* Hochgeladene Fotos anzeigen */}
          {state.photos && state.photos.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-helvetica font-semibold text-primary mb-2">
                Hochgeladene Fotos ({state.photos.length}):
              </p>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {Array.from(state.photos).map((file, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Foto ${index + 1}`}
                      className="w-full h-20 object-cover rounded-md border border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newPhotos = state.photos.filter((_, i) => i !== index);
                        setState({ ...state, photos: newPhotos });
                      }}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ×
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 rounded-b-md">
                      {file.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Dokumente Upload Bereich */}
          <label className="border border-dashed border-primary w-full h-32 rounded-md flex flex-col justify-center items-center gap-2 bg-[#FF6A2B1A] cursor-pointer hover:bg-[#FF6A2B2A] transition-colors">
            <Documents />
            <p className="text-sm font-helvetica text-primary">
              Dokumente hochladen
            </p>
            <p className="text-xs font-helvetica text-primary">PDF</p>
            <input
              type="file"
              className="hidden"
              onChange={(e) => {
                if (e.target.files) {
                  setState({ ...state, documents: Array.from(e.target.files) });
                }
              }}
              multiple
              accept=".pdf,application/pdf"
            />
          </label>

          {/* Hochgeladene Dokumente anzeigen */}
          {state.documents && state.documents.length > 0 && (
            <div className="mt-3">
              <p className="text-sm font-helvetica font-semibold text-primary mb-2">
                Hochgeladene Dokumente ({state.documents.length}):
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Array.from(state.documents).map((file, index) => (
                  <div key={index} className="relative group border border-gray-300 rounded-md p-3 bg-gray-50 hover:bg-gray-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <svg 
                          width="32" 
                          height="32" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          className="text-red-600"
                        >
                          <path 
                            d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            fill="rgba(220, 38, 38, 0.1)"
                          />
                          <path 
                            d="M14 2L20 8H14V2Z" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            fill="rgba(220, 38, 38, 0.2)"
                          />
                          <text 
                            x="12" 
                            y="16" 
                            fontSize="6" 
                            textAnchor="middle" 
                            fill="currentColor" 
                            fontWeight="bold"
                          >
                            PDF
                          </text>
                        </svg>
                      </div>
                      <div className="flex-grow min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024 / 1024).toFixed(2)} MB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          const newDocuments = state.documents.filter((_, i) => i !== index);
                          setState({ ...state, documents: newDocuments });
                        }}
                        className="flex-shrink-0 w-6 h-6 bg-red-500 text-white rounded-full text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="w-full h-[1px] bg-[#FF6A2B4D]" />
          <p className="text-[20px] font-helvetica font-bold text-primary">
            Kontaktinformationen
          </p>
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              name="sure_name"
              value={state.sure_name}
              label="Name"
              placeholder="Eingeben"
              onChange={handleInput}
              required
            />
            <TextInput
              name="email"
              value={state.email}
              label="E-Mail Adresse"
              placeholder="Eingeben"
              onChange={handleInput}
              required
            />
          </div>
          <DropdownInput
            name="telephone_number"
            value={state.telephone_number}
            label="Telefonnummer"
            placeholder="Eingeben"
            data={phoneCodes.map(({ code }) => ({
              label: code,
              value: code,
            })) || []}
            selectedValue="+49"
            onChangeText={handleInput}
            required
          />
          <div className="w-full h-[1px] bg-[#FF6A2B4D]" />
          <Button type="submit" title="Erstellen" variant="primary" />
        </form>
      </div>
      <Loader isLoading={isLoading} text="Please wait..." />
      <Alert
        isPopup={error.isShow}
        handlePopup={() => setError({ ...error, isShow: false })}
        type={error.type}
        text={error.text}
      />
    </Dialog>
  );
};
